import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight } from "lucide-react";

const DEFAULT_CITY_IMAGE = "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=400&h=300&fit=crop&q=75&fm=webp";

export function getCityImage(city: string, citiesData?: any[]): string {
  if (citiesData) {
    const match = citiesData.find((c) => c.name === city);
    if (match?.hero_image_url) return match.hero_image_url;
  }
  return DEFAULT_CITY_IMAGE;
}

export function CityExplorer() {
  // Get cities from DB
  const { data: dbCities } = useQuery({
    queryKey: ["cities-db"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cities")
        .select("name, slug, hero_image_url, hero_image_alt")
        .eq("is_active", true);
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 10,
  });

  const getCitySlug = (cityName: string) => {
    const match = dbCities?.find((c) => c.name === cityName);
    return match?.slug ?? encodeURIComponent(cityName.toLowerCase());
  };

  // Get venue counts per city
  const { data } = useQuery({
    queryKey: ["city-explorer"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("venues")
        .select("city")
        .eq("status", "active");
      if (error) throw error;
      const counts: Record<string, number> = {};
      data.forEach((v) => {
        counts[v.city] = (counts[v.city] || 0) + 1;
      });
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([city, count]) => ({ city, count }));
    },
    staleTime: 1000 * 60 * 10,
  });

  if (!data || data.length === 0) return null;

  const topCities = data.slice(0, 4);
  const moreCities = data.slice(4, 24);

  const getImage = (cityName: string) => getCityImage(cityName, dbCities ?? []);
  const getAlt = (cityName: string) => {
    const match = dbCities?.find((c) => c.name === cityName);
    return match?.hero_image_alt ?? cityName;
  };

  return (
    <section className="container py-12">
      <div className="mb-8">
        <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
          Explora salas por ciudad
        </h2>
        <p className="mt-2 text-muted-foreground">
          Descubre las mejores salas de conciertos en las principales ciudades de España
        </p>
      </div>

      {/* Top cities with images */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {topCities.map(({ city, count }) => (
          <Link
            key={city}
            to={`/salas/${getCitySlug(city)}`}
            className="group relative overflow-hidden rounded-lg aspect-[4/3]"
          >
            <img
              src={getImage(city)}
              alt={getAlt(city)}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 text-primary-foreground">
              <h3 className="font-display text-lg font-semibold">{city}</h3>
              <div className="flex items-center justify-between">
                <span className="text-sm opacity-90">{count} salas disponibles</span>
                <ArrowRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* More cities grid */}
      {moreCities.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-4 font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Más ciudades
          </h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 md:grid-cols-4">
            {moreCities.map(({ city, count }) => (
              <Link
                key={city}
                to={`/salas/${getCitySlug(city)}`}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {city} ({count})
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
